import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import type { CSSProperties } from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import Header from '@/common/header';
import { bai_jamjuree } from '@/public/assets/fonts/Bai-Jamjuree';
import { runAfterLoadWhenIdle } from '@/utils/run-after-load-when-idle';

import { Footer } from '../../footer';
import type { DefaultTrialProps } from '../../trial-layout-switcher';
import { SignupForm } from './components/signup-form';
import { supportsDialog, TrialDialog } from './components/trial-dialog';
import { NAMESPACE } from './components/ui';
import { type GeoData, loadGeoData } from './engine/geo';
import { initCounters, initReveal } from './engine/page-motion';
import { Comparison } from './sections/comparison';
import { Faq } from './sections/faq';
import { FinalCta } from './sections/final-cta';
import { Games } from './sections/games';
import { Hero } from './sections/hero';
import { MultiInternet } from './sections/multi-internet';
import { Multiconnection } from './sections/multiconnection';
import { Scale } from './sections/scale';
import { Wins } from './sections/wins';
import { numberFormatter } from './strings';
import styles from './v3b.module.css';

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

interface CodeRainProps extends DefaultTrialProps {}

/** The geo tables are fetched once, when the first block that needs them nears the viewport, or at idle after `load`. */
function useGeoData(main: HTMLElement | null) {
  const [geo, setGeo] = useState<GeoData | null>(null);
  useEffect(() => {
    if (!main) return undefined;
    let alive = true;
    let started = false;
    const start = () => {
      if (started) return;
      started = true;
      loadGeoData().then((data) => {
        if (alive && data) setGeo(data);
      });
    };
    const targets = ['#vitorias', '#multiconexao', '#multi-internet'].map((selector) => main.querySelector(selector)).filter((el): el is Element => !!el);
    let observer: IntersectionObserver | null = null;
    if (typeof IntersectionObserver === 'function') {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            start();
            observer?.disconnect();
          }
        },
        { rootMargin: '150% 0px' }
      );
      targets.forEach((target) => observer?.observe(target));
    }
    const cancelIdle = runAfterLoadWhenIdle(start);
    return () => {
      alive = false;
      observer?.disconnect();
      cancelIdle();
    };
  }, [main]);
  return geo;
}

export function CodeRain({ daysTrial, trialConfigId, trialPageId, isDisabledButton = false }: CodeRainProps) {
  const { t } = useTranslation(NAMESPACE);
  const { locale = 'pt' } = useRouter();
  const [main, setMain] = useState<HTMLElement | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formHost, setFormHost] = useState<HTMLDivElement | null>(null);
  const heroSlotRef = useRef<HTMLDivElement>(null);
  const dialogBodyRef = useRef<HTMLDivElement>(null);
  const geo = useGeoData(main);

  // ONE signup form for the whole page. It renders into a detached node that is moved between the hero
  // and the trial dialog (the design moves the form element itself), so what the visitor typed, the
  // captcha token and the validation state survive both trips.
  useIsomorphicLayoutEffect(() => {
    const host = document.createElement('div');
    host.className = 'np-form-host';
    setFormHost(host);
    return () => host.remove();
  }, []);
  useIsomorphicLayoutEffect(() => {
    if (!formHost) return;
    const target = dialogOpen ? dialogBodyRef.current : heroSlotRef.current;
    if (target && formHost.parentElement !== target) target.appendChild(formHost);
    // the locale re-mounts the page (see the root's key), which brings a new hero slot
  }, [formHost, dialogOpen, locale]);

  useEffect(() => {
    if (!main) return undefined;
    const stopReveal = initReveal(main);
    const stopCounters = initCounters(main, numberFormatter(locale));
    return () => {
      stopReveal();
      stopCounters();
    };
  }, [main, locale]);

  const openTrial = useCallback(() => {
    if (supportsDialog()) {
      setDialogOpen(true);
      return;
    }
    // no <dialog>: take the visitor to the hero form instead
    const slot = heroSlotRef.current;
    slot?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => slot?.querySelector<HTMLInputElement>('input:not([type=hidden])')?.focus({ preventScroll: true }), 450);
  }, []);
  const closeTrial = useCallback(() => setDialogOpen(false), []);

  return (
    <div key={locale} className={`${styles.root} ${bai_jamjuree.className}`} style={{ '--v3b-font': bai_jamjuree.style?.fontFamily } as CSSProperties}>
      <Header variant='primary' />
      <main ref={setMain} className={styles.page} id='conteudo'>
        <Hero formSlotRef={heroSlotRef} />
        <Wins />
        <Multiconnection geo={geo} onCta={openTrial} />
        <MultiInternet geo={geo} onCta={openTrial} />
        <Comparison onCta={openTrial} />
        <Scale />
        <Games />
        <Faq onCta={openTrial} />
        <FinalCta onCta={openTrial} />
        <TrialDialog open={dialogOpen} onClose={closeTrial} bodyRef={dialogBodyRef} />
      </main>
      <p className='mx-auto mt-4 px-5 text-center text-[.75rem] text-[#9AA3B5]'>{t('rating_source')}</p>
      <Footer text={t('footer', { year: new Date().getFullYear() })} />
      {formHost && createPortal(<SignupForm daysTrial={daysTrial} trialConfigId={trialConfigId} trialPageId={trialPageId} isDisabledButton={isDisabledButton} />, formHost)}
    </div>
  );
}
